import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { MdCheckCircle } from "react-icons/md";

import FileUploader from "../shared/TailwindComponents/FileUploader";
import Input from "../shared/TailwindComponents/Input";
import Dropdown, { DropdownOption } from "../shared/TailwindComponents/Dropdown";
import { FolderProps } from "./documents/types";
import Button from "../shared/TailwindComponents/Button";
import { useHistory } from "react-router-dom";
import { RootState, useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { importFilesSSE } from "../../store/actions/FolderAction";
import {  FILE_IMPORT_RESET } from "../../store/types";
import { ShareUser } from "../shared/ShareAccessManager";
import CollaboratorSelector, { Collaborator } from "../shared/CollaboratorSelector";

// Import validation system
import { createFolderSchema, sanitizeString } from "../../utils/validation";
import { FieldError } from "../shared/FormComponents";
import FieldShell from "../shared/TailwindComponents/FieldShell";
import Spinner from '../shared/TailwindComponents/Spinner';

type Props = {
    onClose: () => void;
    existingFolders: FolderProps[];
    selectedFolderItem?: any;
    handleCreateFolder: (data: {
        folderId?: string;
        folderName?: string;
        files: File[];

    }) => void | Promise<void>;
    employees: ShareUser[]
    initialShared: ShareUser[]
    onRefresh?: (createdFolderId?: string) => void | Promise<void>;
};

const CreateFolder: React.FC<Props> = ({
    onClose,
    existingFolders,
    selectedFolderItem,
    employees,
    initialShared,
    onRefresh
}) => {
    const { t } = useTranslation();
    const [toggle, setToggle] = useState('existing')
    const history = useHistory()
    const dispatch = useAppDispatch()
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const { isConnecting, connected, steps, ok, summary, complete } = useSelector((state: RootState) => state.folders)

    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [recievedFolderID, setRecievedFolderID] = useState(null)
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<string>("");

    useEffect(() => {
        const initialCollaborators = initialShared.map(user => ({
            user_id: user.id,
            access: 'edit' as const
        }));
        setCollaborators(initialCollaborators);
    },[initialShared])

    useEffect(() => {
        setResult(summary)
        if (summary && summary.folderId) {
            setRecievedFolderID(summary.folderId)
        }
        if (summary && onRefresh) {
            // Pass the folder ID to the refresh callback for proper navigation
            if (summary.folderId) {
              onRefresh(summary.folderId);
            } else {
              onRefresh();
            }
        }
    }, [summary, onRefresh])

    const folderOptions = useMemo(() => {
        const options =
            (existingFolders || []).filter(f => f.FolderType !== 'Locked').map((f) => ({
                label:
                    (f as any).FolderName ??
                    (f as any).name ??
                    (f as any).title ??
                    String(f.id ?? f.FolderID ?? ""),
                value: String(f.id ?? f.FolderID ?? ""),
            })) || [];

        if (
            selectedFolderItem && options.length >= 0
        ) {
            options.push({
                label: selectedFolderItem.FolderName,
                value: selectedFolderItem.FolderID,
            });
        }

        return options;
    }, [existingFolders, selectedFolderItem]);

    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            FolderId: Yup.string().optional(),
            FolderName: Yup.string().trim().optional(),
            Files: Yup.array()
        }).test(
            "one-of-two",
            t("Please select an existing folder or enter a new folder name."),
            (values: any) => Boolean(values?.FolderId || values?.FolderName)
        );
    }, [t]);

    const formik = useFormik({
        initialValues: {
            FolderId: (selectedFolderItem && selectedFolderItem?.FolderID) || "",
            FolderName: "",
            Files: []
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setIsSubmitting(true);
                setResult(null);

                // Sanitize folder name
                const sanitizedFolderName = values.FolderName
                  ? sanitizeString(values.FolderName, 255, false)
                  : '';

                // Prepare collaborators with access levels
                const validCollaborators = collaborators
                    .map(c => ({
                        user_id: parseInt(String(c.user_id), 10),
                        access: c.access
                    }))
                    .filter(c => !Number.isNaN(c.user_id));

                await dispatch(
                    importFilesSSE(
                        {
                            FolderID: values.FolderId,
                            FolderName: sanitizedFolderName,
                            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined
                        },
                        values.Files
                    )
                );
            } catch (e: any) {
                setError(e?.message || t("Network error"));
            } finally {
                setIsSubmitting(false);
            }
        },

    });
    const handleClose = async () => {
        if (recievedFolderID) {
            history.push(`/files/${recievedFolderID}`)
        } else if (onRefresh) {
            // Refresh the folder list if not navigating
            await onRefresh();
        }
        dispatch({ type: FILE_IMPORT_RESET });
        onClose()
    }
    if (result && !isSubmitting) {
        return (
            <div className="flex flex-col px-6 py-6 w-[700px] max-h-[90vh] overflow-hidden gap-6">
                {/* Header - static */}
                <div className="flex-shrink-0">
                    <h3 className="text-lg font-semibold">
                        {result.ok
                            ? result.rejectedCount > 0
                                ? t("Some files uploaded")
                                : t("Upload complete")
                            : t("Upload failed")}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {result.message || error || ""}
                    </p>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto space-y-6 min-h-0">
                    {!!result.filesUploaded?.length && (
                        <div>
                            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
                                {t("Uploaded")} ({result.filesUploaded.length})
                            </div>
                            <div className="space-y-2">
                                {result.filesUploaded.map((f: any) => (
                                    <div
                                        key={f.FileID}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800"
                                    >
                                        <MdCheckCircle className="flex-shrink-0 h-5 w-5 text-success-600" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                                {f.OriginalName}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                {(f.Size / 1024).toFixed(1)} KB
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!!result.filesRejected?.length && (
                        <div>
                            <div className="font-medium mb-3 text-gray-900 dark:text-gray-100">
                                {t("Rejected")} ({result.filesRejected.length})
                            </div>
                            <div className="space-y-2">
                                {result.filesRejected.map((r: any, i: any) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-950/20 border border-danger-200 dark:border-danger-800"
                                    >
                                        <div className="flex-shrink-0 h-5 w-5 text-danger-600 mt-0.5">✕</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                                {r.OriginalName}
                                            </p>
                                            <p className="text-xs text-danger-600 dark:text-danger-400">
                                                {r.Reason}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer button - static */}
                <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        type="button"
                        variant="primary"
                        size="small"
                        onClick={handleClose}
                    >
                        {t("Done")}
                    </Button>
                </div>
            </div>
        )
    }
    if (isSubmitting) {
        return (
            <div className="flex flex-col items-center justify-center w-[700px] max-h-[90vh]">
                <div className="flex flex-col items-center gap-4">
                    <Spinner size="sm" className="!h-12 !w-12" />
                    <p className="text-gray-600 dark:text-gray-400">{t("Importing files...")}</p>
                </div>
            </div>
        )
    }



    return (
        <form onSubmit={formik.handleSubmit} className="flex flex-col w-[700px] max-h-[90vh] overflow-hidden">
            {/* Header section - not scrollable */}
            <div className="flex-shrink-0 px-6 pb-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
                {/* Row 1: Toggle and Path Dropdown on same row */}
                <div className="flex items-start gap-4">
                    {/* Toggle — a tab bar, so it wears the app's tab treatment
                        (.glass-bar + .nav-pill) rather than its own colours. */}
                    <div className="flex-shrink-0">
                        <FieldShell label={t("Folder")}>
                            <div className="glass-bar inline-flex gap-1 rounded-lg border border-hairline p-1" role="tablist">
                                {([
                                    { key: 'existing', label: t("Use existing") },
                                    { key: 'new', label: t("Create new") },
                                ]).map(({ key, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        role="tab"
                                        aria-selected={toggle === key}
                                        onClick={() => setToggle(key)}
                                        className={clsx(
                                            "nav-pill rounded-md px-3 py-1.5 text-sm font-medium",
                                            toggle === key
                                                ? "nav-pill-active text-fg-strong"
                                                : "text-fg-muted hover:text-fg-body"
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </FieldShell>
                    </div>

                    {/* Path Dropdown - 60% width, always visible */}
                    <div className="flex flex-col flex-1">
                        <Dropdown
                            label={toggle === 'new' ? t("Parent Folder") : t("Path")}
                            description={
                                toggle === 'new'
                                    ? t("Leave empty to create the folder at the root level.")
                                    : undefined
                            }
                            infoText={
                                toggle === 'new'
                                    ? t("Optionally select a parent folder for the new folder")
                                    : t('You can import files by selecting any folder.')
                            }
                            options={folderOptions}
                            value={formik.values.FolderId}
                            onChange={(val) => {
                                formik.setFieldValue("FolderId", val);
                                setSelectedFolderId((Array.isArray(val) ? val[0] : val) || "");
                            }}
                            error={formik.touched.FolderId ? (formik.errors as any).FolderId : undefined}
                        />
                    </div>
                </div>

                {/* Row 2: Folder Name - only show for "Create new" mode */}
                {toggle === 'new' && (
                    <Input
                        label={t("Folder Name")}
                        type="text"
                        name="FolderName"
                        value={formik.values.FolderName}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.FolderName
                                ? (formik.errors as any).FolderName ||
                                (formik.errors as any)?.["one-of-two"]
                                : undefined
                        }
                        placeholder={t("e.g., Contracts 2025")}
                    />
                )}
            </div>

            {/* Scrollable content section */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                    {!formik.values.FolderId &&
                        !formik.values.FolderName &&
                        (formik.errors as any)?.["one-of-two"] && (
                            <FieldError error={(formik.errors as any)["one-of-two"]} />
                        )}

                    {/* Share with team members section - using unified CollaboratorSelector */}
                    <CollaboratorSelector
                        collaborators={collaborators}
                        onCollaboratorsChange={setCollaborators}
                        initialShared={selectedFolderId ? initialShared : []}
                    />

                    {/* File uploader section */}
                    <FieldShell
                        label={t("Import Files")}
                        helpText={t("Unsupported files will be rejected automatically.")}
                    >
                        <FileUploader files={formik.values.Files} onFilesChange={(newFiles: File[]) => formik.setFieldValue("Files", newFiles)} />
                    </FieldShell>
                </div>
            </div>

            {/* Footer with button - not scrollable */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div className="flex justify-end">
                    <Button variant='primary' size='small' type="submit" disabled={isSubmitting}>
                        {
                            isSubmitting ? t('processing...') : t('Import')
                        }
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default CreateFolder;
